import { useEffect, useState } from 'react';
import { FileText, Link2, ArrowDown } from 'lucide-react';
import { useT } from '../../i18n';
import { docApi, docHref, type RegisterItem } from './api';
import { attachedToLabel } from './DocumentsRegister';
import { DocumentsAdd, fmtDocDate } from './DocumentsList';

// [Proposition: documents] Lighter "Documents" section for the project page's
// side panel: the number of documents and the 5 most recent, each with what
// it's attached to. "Voir tous les documents" opens the full register (in the
// follow-up view once the project runs, at the bottom of Aperçu before) when
// `onShowAll` is given; otherwise the list unfolds here. With `canAdd`, files
// and links can be added here, as documents of the whole project. Same muted
// side-panel markup as ProjectDocuments (.pdp-docs*).
// On the Execution tab (`compact`), where the register is already on the page,
// only the count and a link down to it — the list isn't shown twice.

const LATEST = 5;

export function DocumentsSummary({ projectId, onShowAll, compact = false, canAdd = false, reloadKey, onChanged }: {
  projectId: number;
  onShowAll?: () => void;
  /** On the Execution tab, where the full register is already on the page: just the count and a link down to it. */
  compact?: boolean;
  /** Show "Joindre un fichier" / "Ajouter un lien" (documents of the whole project). */
  canAdd?: boolean;
  /** Changes when documents were added elsewhere on the page. */
  reloadKey?: unknown;
  /** After a document was added here. */
  onChanged?: () => void;
}) {
  const { t } = useT();
  const [docs, setDocs] = useState<RegisterItem[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;
    docApi.register(projectId)
      .then(d => { if (active) setDocs([...d].sort((a, b) => b.added_at.localeCompare(a.added_at))); })
      .catch(() => { if (active) setDocs([]); });
    return () => { active = false; };
  }, [projectId, reloadKey, reloads]);

  const addBar = canAdd && (
    <DocumentsAdd target={{ type: 'project', id: projectId }}
      onAdded={() => { setReloads(n => n + 1); onChanged?.(); }} />
  );

  const shown = docs ? (expanded ? docs : docs.slice(0, LATEST)) : [];

  if (compact && onShowAll) {
    return (
      <div className="detail-section docs-summary-compact" style={{ marginBottom: 'var(--s16)' }}>
        <span className="detail-label">{t('documents.title')}{docs ? ` (${docs.length})` : ''}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onShowAll}>
          {t('docs.summaryGoToRegister')} <ArrowDown size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="detail-section" style={{ marginBottom: 'var(--s16)' }}>
      <div className="detail-label" style={{ marginBottom: 'var(--s8)' }}>
        {t('documents.title')}{docs ? ` (${docs.length})` : ''}
      </div>
      {docs == null ? (
        <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('common.loading')}</div>
      ) : docs.length === 0 ? (
        <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('documents.empty')}</div>
      ) : (
        <>
          {docs.length > LATEST && !expanded && (
            <div className="text-ink-3" style={{ fontSize: 'var(--t-11)', marginBottom: 'var(--s4)' }}>{t('docs.summaryLatest', { n: LATEST })}</div>
          )}
          <div className="pdp-docs">
            {shown.map(d => (
              <a key={d.id} href={docHref(d)} className="pdp-doc" title={d.name} target="_blank" rel="noopener noreferrer">
                <span className="pdp-doc-icon">{d.kind === 'link' ? <Link2 size={14} /> : <FileText size={14} />}</span>
                <span className="pdp-doc-main">
                  <span className="pdp-doc-name">{d.name}</span>
                  <span className="pdp-doc-by">{attachedToLabel(d, t)} · {fmtDocDate(d.added_at)}</span>
                </span>
              </a>
            ))}
          </div>
          {onShowAll ? (
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--s4)' }} onClick={onShowAll}>
              {t('docs.summaryShowAll')}
            </button>
          ) : docs.length > LATEST && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--s4)' }} onClick={() => setExpanded(v => !v)}>
              {expanded ? t('docs.summaryShowLess') : t('docs.summaryShowAll')}
            </button>
          )}
        </>
      )}
      {addBar && <div className="docs-summary-add">{addBar}</div>}
    </div>
  );
}
